"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import FileUploader from "./FileUploader";
import ProcessingOverlay from "./ProcessingOverlay";
import SignupGateModal from "./SignupGateModal";
import UsageLimitBanner from "./UsageLimitBanner";
import { useToolFetch } from "@/hooks/useToolFetch";
import { useAuth } from "@/lib/auth";
import { SIGNATURE_FONTS, FONT_LOAD_URL_PRIMARY, FONT_LOAD_URL_DEFERRED, SIGNATURE_COLORS } from "@/lib/signatureFonts";
import type { SavedSignature } from "@/lib/types";

const DrawPad = dynamic(() => import("@/components/signing/DrawPad"), { ssr: false });
const UploadSignature = dynamic(() => import("@/components/signing/UploadSignature"), { ssr: false });

interface Placement {
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  type: "signature";
  imageData: string;
  displayUrl: string;
}

type Tab = "type" | "draw" | "upload";

export default function AddSignatureTool() {
  const { isAuthenticated } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [activeTab, setActiveTab] = useState<Tab>("type");
  const [typeName, setTypeName] = useState("");
  const [selectedFont, setSelectedFont] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);
  const [fontSize, setFontSize] = useState(48);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ url: string; size: number } | null>(null);
  const [error, setError] = useState("");
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [showSigPanel, setShowSigPanel] = useState(true);
  const [savedSignatures, setSavedSignatures] = useState<SavedSignature[]>([]);
  const [savingSignature, setSavingSignature] = useState(false);
  const [signatureSaved, setSignatureSaved] = useState(false);
  const [pendingSaveNotice, setPendingSaveNotice] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { toolFetch, quotaError } = useToolFetch();
  // Drag state
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const dragStart = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);
  const justDragged = useRef(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const pendingSaveAttempted = useRef(false);

  // Load fonts — non-render-blocking via media="print" trick
  useEffect(() => {
    for (const origin of ["https://fonts.googleapis.com", "https://fonts.gstatic.com"]) {
      if (!document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
        const pc = document.createElement("link");
        pc.rel = "preconnect";
        pc.href = origin;
        pc.crossOrigin = "anonymous";
        document.head.appendChild(pc);
      }
    }
    const loadFont = (url: string) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = url;
      link.media = "print";
      link.onload = () => { link.media = "all"; };
      document.head.appendChild(link);
    };
    loadFont(FONT_LOAD_URL_PRIMARY);
    const deferTimer = setTimeout(() => loadFont(FONT_LOAD_URL_DEFERRED), 1000);
    const timeout = setTimeout(() => setFontsLoaded(true), 2000);
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => setFontsLoaded(true));
    }
    return () => { clearTimeout(deferTimer); clearTimeout(timeout); };
  }, []);

  // Auto-save pending signature after login/register redirect
  useEffect(() => {
    if (pendingSaveAttempted.current || !isAuthenticated) return;
    const pending = typeof window !== "undefined" ? localStorage.getItem("sf_pending_tool_signature") : null;
    if (!pending) return;
    pendingSaveAttempted.current = true;
    try {
      const { image, sigName } = JSON.parse(pending);
      if (!image) return;
      localStorage.removeItem("sf_pending_tool_signature");
      const accessToken = localStorage.getItem("sf_access_token");
      if (!accessToken) return;
      setSavingSignature(true);
      fetch("/api/auth/me/signatures", {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name: sigName || "My signature", image_base64: image }),
      })
        .then((res) => {
          if (res.ok) {
            setPendingSaveNotice("Signature saved to your account!");
            setTimeout(() => setPendingSaveNotice(""), 4000);
            // Refresh saved signatures list
            return fetch("/api/auth/me/signatures", {
              headers: { Authorization: `Bearer ${accessToken}` },
            }).then((r) => r.ok ? r.json() : []).then((data) => {
              setSavedSignatures(Array.isArray(data) ? data : []);
            });
          }
        })
        .catch(() => {})
        .finally(() => setSavingSignature(false));
    } catch {
      localStorage.removeItem("sf_pending_tool_signature");
    }
  }, [isAuthenticated]);

  // Fetch saved signatures if authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setSavedSignatures([]);
      return;
    }
    const accessToken = localStorage.getItem("sf_access_token");
    if (!accessToken) return;
    fetch("/api/auth/me/signatures", {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setSavedSignatures(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [isAuthenticated]);

  // Auto-download when result is ready
  useEffect(() => {
    if (!result) return;
    const a = document.createElement("a");
    a.href = result.url;
    a.download = "signed.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [result]);

  // Render typed signature on canvas preview
  useEffect(() => {
    if (activeTab !== "type") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;

    const dpr = window.devicePixelRatio || 1;
    const displayWidth = canvas.clientWidth;
    const displayHeight = canvas.clientHeight;
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, displayWidth, displayHeight);

    if (!typeName.trim()) {
      ctx.fillStyle = "#94a3b8";
      ctx.font = "16px system-ui";
      ctx.textAlign = "center";
      ctx.fillText("Your signature will appear here", displayWidth / 2, displayHeight / 2 + 6);
      return;
    }

    ctx.fillStyle = SIGNATURE_COLORS[selectedColor].value;
    ctx.font = `${fontSize}px ${SIGNATURE_FONTS[selectedFont].family}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(typeName, displayWidth / 2, displayHeight / 2);
  }, [activeTab, typeName, selectedFont, selectedColor, fontSize, fontsLoaded]);

  // Generate typed signature as tight-fit PNG
  const generateTypedSignature = useCallback(() => {
    if (!typeName.trim()) return;
    const dpr = 2;
    const pad = 20;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    ctx.font = `${fontSize}px ${SIGNATURE_FONTS[selectedFont].family}`;
    const metrics = ctx.measureText(typeName);
    const textWidth = metrics.width;
    const textHeight = fontSize * 1.4;
    canvas.width = (textWidth + pad * 2) * dpr;
    canvas.height = (textHeight + pad * 2) * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = SIGNATURE_COLORS[selectedColor].value;
    ctx.font = `${fontSize}px ${SIGNATURE_FONTS[selectedFont].family}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(typeName, textWidth / 2 + pad, textHeight / 2 + pad);
    setSignatureDataUrl(canvas.toDataURL("image/png"));
    setShowSigPanel(false);
    setSignatureSaved(false);
  }, [typeName, selectedFont, selectedColor, fontSize]);

  const handleDrawChange = useCallback((dataUrl: string | null) => {
    setSignatureDataUrl(dataUrl ? dataUrl : null);
  }, []);

  const handleUploadChange = useCallback((dataUrl: string | null) => {
    setSignatureDataUrl(dataUrl ? dataUrl : null);
  }, []);

  const useSavedSignature = useCallback((sig: SavedSignature) => {
    if (sig.image_base64) {
      setSignatureDataUrl(sig.image_base64);
      setShowSigPanel(false);
      setSignatureSaved(true); // already saved — don't offer to save again
    }
  }, []);

  // Save signature — direct API for auth, localStorage stash for anon
  const saveSignature = useCallback(() => {
    if (!signatureDataUrl) return;
    const accessToken = typeof window !== "undefined" ? localStorage.getItem("sf_access_token") : null;
    if (!accessToken || !isAuthenticated) {
      // Stash signature in localStorage and redirect to register
      localStorage.setItem("sf_pending_tool_signature", JSON.stringify({
        image: signatureDataUrl,
        sigName: typeName.trim() || "My signature",
      }));
      window.location.href = "/register?redirect=/tools/add-signature-to-pdf";
      return;
    }
    setSavingSignature(true);
    fetch("/api/auth/me/signatures", {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ name: typeName.trim() || "My signature", image_base64: signatureDataUrl }),
    })
      .then((res) => {
        if (res.ok) {
          setSignatureSaved(true);
          // Refresh list
          return fetch("/api/auth/me/signatures", {
            headers: { Authorization: `Bearer ${accessToken}` },
          }).then((r) => r.ok ? r.json() : []).then((data) => {
            setSavedSignatures(Array.isArray(data) ? data : []);
          });
        } else {
          return res.json().catch(() => ({})).then((d) => {
            setError(d.detail || "Failed to save signature");
          });
        }
      })
      .catch(() => setError("Failed to save signature"))
      .finally(() => setSavingSignature(false));
  }, [signatureDataUrl, isAuthenticated, typeName]);

  const handleFilesSelected = useCallback((files: File[]) => {
    setFile(files[0] || null);
    setPages([]);
    setPlacements([]);
    setResult(null);
    setError("");
    setCurrentPage(0);
  }, []);

  // Render PDF pages
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    setLoadingPdf(true);

    const render = async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf.mjs");
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
          import.meta.url
        ).toString();
        const data = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data }).promise;
        const imgs: string[] = [];
        for (let i = 0; i < pdf.numPages; i++) {
          const page = await pdf.getPage(i + 1);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext("2d")!, viewport, canvas }).promise;
          imgs.push(canvas.toDataURL("image/jpeg", 0.85));
          if (cancelled) return;
        }
        setPages(imgs);
      } catch {
        setError("Failed to load PDF");
      } finally {
        if (!cancelled) setLoadingPdf(false);
      }
    };
    render();
    return () => { cancelled = true; };
  }, [file]);

  // Place signature on PDF page
  const handlePageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!signatureDataUrl || draggingIdx !== null) return;
      if (justDragged.current) { justDragged.current = false; return; }
      const rect = e.currentTarget.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      const w = 0.2;
      const h = 0.06;

      const b64 = signatureDataUrl.split(",")[1];

      setPlacements((prev) => [
        ...prev,
        {
          page: currentPage,
          x: Math.max(0, Math.min(1 - w, x - w / 2)),
          y: Math.max(0, Math.min(1 - h, y - h / 2)),
          w, h,
          type: "signature",
          imageData: b64,
          displayUrl: signatureDataUrl,
        },
      ]);
    },
    [signatureDataUrl, currentPage, draggingIdx]
  );

  // Drag handlers
  const handleDragStart = useCallback((e: React.MouseEvent, idx: number) => {
    e.stopPropagation();
    e.preventDefault();
    const container = pdfContainerRef.current;
    if (!container) return;
    const p = placements[idx];
    dragStart.current = {
      mx: e.clientX,
      my: e.clientY,
      ox: p.x,
      oy: p.y,
    };
    setDraggingIdx(idx);
  }, [placements]);

  useEffect(() => {
    if (draggingIdx === null) return;
    const container = pdfContainerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStart.current) return;
      const rect = container.getBoundingClientRect();
      const dx = (e.clientX - dragStart.current.mx) / rect.width;
      const dy = (e.clientY - dragStart.current.my) / rect.height;
      const p = placements[draggingIdx];
      const newX = Math.max(0, Math.min(1 - p.w, dragStart.current.ox + dx));
      const newY = Math.max(0, Math.min(1 - p.h, dragStart.current.oy + dy));

      setPlacements((prev) =>
        prev.map((pl, i) => i === draggingIdx ? { ...pl, x: newX, y: newY } : pl)
      );
    };

    const handleMouseUp = () => {
      dragStart.current = null;
      setDraggingIdx(null);
      justDragged.current = true;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [draggingIdx, placements]);

  const removePlacement = useCallback((idx: number) => {
    setPlacements((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const submit = useCallback(async () => {
    if (!file || placements.length === 0) {
      setError("Place at least one signature on the document");
      return;
    }
    setProcessing(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      const backendPlacements = placements.map(({ displayUrl, ...rest }) => rest);
      formData.append("placements", JSON.stringify(backendPlacements));

      const res = await toolFetch("/api/tools/add-signature", { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to add signature");
      }

      const blob = await res.blob();
      setResult({ url: URL.createObjectURL(blob), size: blob.size });
    } catch (e: any) {
      setError(e.message || "Failed to add signature");
    } finally {
      setProcessing(false);
    }
  }, [file, placements]);

  const reset = useCallback(() => {
    setFile(null);
    setPages([]);
    setPlacements([]);
    setResult(null);
    setError("");
    setSignatureDataUrl(null);
    setShowSigPanel(true);
    setSignatureSaved(false);
  }, []);

  if (quotaError?.gate === "signup") return <SignupGateModal quotaError={quotaError} />;

  if (!file) {
    return (
      <div className="space-y-6">
        {quotaError?.gate === "limit" && <UsageLimitBanner quotaError={quotaError} />}
        {/* Auto-save success notice after redirect */}
        {pendingSaveNotice && (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 animate-fade-in">
            <svg className="h-4 w-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium text-emerald-700">{pendingSaveNotice}</span>
          </div>
        )}
        {!isAuthenticated && (
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white/60 px-3 py-2">
            <svg className="h-4 w-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
            </svg>
            <span className="text-xs text-slate-500">
              Have a saved signature?{" "}
              <Link href="/login?redirect=/tools/add-signature-to-pdf" className="text-[var(--color-gold)] font-medium hover:underline">
                Sign in first to use it
              </Link>
            </span>
          </div>
        )}
        <FileUploader accept=".pdf" onFilesSelected={handleFilesSelected} label="Drop your PDF here" description="Add your signature to any page" />
      </div>
    );
  }

  if (loadingPdf) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <div className="relative h-8 w-8">
            <div className="absolute inset-0 rounded-full border-2 border-slate-200" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-brand-600 animate-spin" />
          </div>
          <p className="text-sm text-slate-500">Loading PDF...</p>
        </div>
      </div>
    );
  }

  const hasSaved = savedSignatures.length > 0;

  return (
    <div className="relative space-y-6">
      {processing && <ProcessingOverlay message="Adding signature..." />}

      {/* Inline success banner (auto-download already triggered) */}
      {result && (
        <div className="space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 flex-shrink-0">
                <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-emerald-900">Signed PDF downloaded!</p>
                <p className="text-xs text-emerald-600">
                  signed.pdf ({(result.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={result.url} download="signed.pdf" className="btn-primary py-2 px-4 text-sm">
                Download again
              </a>
              <button onClick={reset} className="btn-secondary py-2 px-4 text-sm">
                Process another file
              </button>
            </div>
          </div>

          {/* Two side-by-side CTA cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Store agreement securely */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 mb-3">
                <svg className="h-5 w-5 text-violet-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-slate-800">Stop losing signed documents</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Can&apos;t find that contract 6 months from now? Digging through emails and downloads?
                Store all your signed agreements in one secure place — accessible anytime you need them.
              </p>
              <Link
                href={isAuthenticated ? "/dashboard/locker" : "/register?redirect=/dashboard/locker"}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-ink)] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[var(--color-ink-light)] transition-colors"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
                {isAuthenticated ? "Store in Locker" : "Store securely — free"}
              </Link>
            </div>

            {/* Send for e-signature */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 mb-3">
                <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-slate-800">Need someone else to sign?</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Send it for e-signature in 30 seconds — legally binding, with full audit trail
                and tamper-proof certificate. No more printing and scanning.
              </p>
              <Link
                href={isAuthenticated ? "/dashboard/envelopes/new" : "/register?redirect=/dashboard/envelopes/new"}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-gold)] px-4 py-1.5 text-xs font-semibold text-[var(--color-ink)] hover:bg-[var(--color-gold-light)] transition-colors"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                </svg>
                Send for e-signature — free
              </Link>
            </div>
          </div>

          {/* Save signature — compact row below the cards */}
          {signatureDataUrl && !signatureSaved && (
            <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3">
              <svg className="h-4 w-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
              </svg>
              <span className="text-xs text-slate-600 flex-1">
                Save your signature so you never have to recreate it
              </span>
              <button
                onClick={saveSignature}
                disabled={savingSignature}
                className="flex-shrink-0 inline-flex items-center gap-1 rounded-md bg-[var(--color-ink)] px-3 py-1 text-xs font-semibold text-white hover:bg-[var(--color-ink-light)] transition-colors disabled:opacity-50"
              >
                {savingSignature ? "Saving..." : isAuthenticated ? "Save signature" : "Sign up & save"}
              </button>
            </div>
          )}
          {signatureDataUrl && signatureSaved && (
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-2.5">
              <svg className="h-4 w-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-emerald-700">Signature saved to your account</span>
            </div>
          )}

        </div>
      )}

      {/* Signature creation panel */}
      {showSigPanel && !result && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-4">

          {/* Saved signatures section */}
          {isAuthenticated && hasSaved && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
              <p className="text-xs font-semibold text-emerald-700 mb-2">
                Saved Signatures ({savedSignatures.length})
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {savedSignatures.map((sig) => (
                  <div
                    key={sig.id}
                    className="relative flex-shrink-0 rounded-lg border border-emerald-200 bg-white p-2 cursor-pointer hover:border-emerald-400 hover:shadow-sm transition-all group"
                    style={{ minWidth: 100, maxWidth: 140 }}
                    onClick={() => useSavedSignature(sig)}
                  >
                    <div className="flex items-center justify-center" style={{ height: 40 }}>
                      {sig.image_base64 ? (
                        <img src={sig.image_base64} alt={sig.name} className="max-h-full max-w-full object-contain" />
                      ) : (
                        <span className="text-[10px] text-slate-400">No preview</span>
                      )}
                    </div>
                    <p className="text-[9px] text-slate-500 mt-1 truncate text-center">{sig.name}</p>
                    {sig.is_default && (
                      <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-emerald-500 flex items-center justify-center">
                        <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab buttons */}
          <div className="flex border-b border-slate-200">
            {([
              { key: "type" as Tab, label: "Type", icon: "M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" },
              { key: "draw" as Tab, label: "Draw", icon: "M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" },
              { key: "upload" as Tab, label: "Upload", icon: "M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" },
            ]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setSignatureDataUrl(null); }}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? "border-[var(--color-gold)] text-[var(--color-ink)]"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                  </svg>
                  {tab.label}
                </span>
              </button>
            ))}
          </div>

          {/* Type tab */}
          {activeTab === "type" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Your name</label>
                <input
                  type="text"
                  value={typeName}
                  onChange={(e) => setTypeName(e.target.value)}
                  placeholder="John Smith"
                  className="input-field"
                  maxLength={60}
                />
              </div>

              <div className="rounded-xl border border-slate-200 overflow-hidden" style={{ background: "repeating-conic-gradient(#f1f5f9 0% 25%, white 0% 50%) 50% / 16px 16px" }}>
                <canvas ref={canvasRef} className="w-full" style={{ height: 120 }} />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Font style</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {SIGNATURE_FONTS.map((font, i) => (
                    <button
                      key={font.name}
                      onClick={() => setSelectedFont(i)}
                      className={`rounded-lg border-2 px-3 py-2.5 text-left transition-all duration-150 ${
                        i === selectedFont
                          ? "border-[var(--color-gold)] bg-[var(--color-gold-muted)]/30"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <span className="text-xl text-slate-800 block whitespace-nowrap py-1" style={{ fontFamily: font.family, color: SIGNATURE_COLORS[selectedColor].value, lineHeight: 2.2 }}>
                        {typeName.trim() || "Signature"}
                      </span>
                      <span className="text-[10px] text-slate-400 mt-0.5 block">{font.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Color</label>
                  <div className="flex gap-2">
                    {SIGNATURE_COLORS.map((color, i) => (
                      <button
                        key={color.name}
                        onClick={() => setSelectedColor(i)}
                        className={`h-8 w-8 rounded-full border-2 transition-all ${
                          i === selectedColor ? "border-[var(--color-gold)] scale-110 shadow-md" : "border-slate-200"
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Size: {fontSize}px
                  </label>
                  <input
                    type="range"
                    min={32}
                    max={80}
                    value={fontSize}
                    onChange={(e) => setFontSize(Number(e.target.value))}
                    className="w-full accent-[var(--color-gold)]"
                  />
                </div>
              </div>

              <button onClick={generateTypedSignature} disabled={!typeName.trim()} className="btn-primary w-full py-2.5">
                Use this signature
              </button>
            </div>
          )}

          {/* Draw tab */}
          {activeTab === "draw" && (
            <div className="space-y-3">
              <div className="min-h-[200px]">
                <DrawPad onSignatureChange={handleDrawChange} />
              </div>
              <button
                onClick={() => { if (signatureDataUrl) { setShowSigPanel(false); setSignatureSaved(false); } }}
                disabled={!signatureDataUrl}
                className="btn-primary w-full py-2.5"
              >
                Use this signature
              </button>
            </div>
          )}

          {/* Upload tab */}
          {activeTab === "upload" && (
            <div className="space-y-3">
              <div className="min-h-[200px]">
                <UploadSignature onSignatureChange={handleUploadChange} />
              </div>
              <button
                onClick={() => { if (signatureDataUrl) { setShowSigPanel(false); setSignatureSaved(false); } }}
                disabled={!signatureDataUrl}
                className="btn-primary w-full py-2.5"
              >
                Use this signature
              </button>
            </div>
          )}
        </div>
      )}

      {/* Signature preview bar with save CTA */}
      {signatureDataUrl && !showSigPanel && !result && (
        <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <img src={signatureDataUrl} alt="Your signature" className="h-10 object-contain" />
          <span className="text-sm text-emerald-700 flex-1">Click on the PDF to place your signature</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!signatureSaved && (
              <button
                onClick={saveSignature}
                disabled={savingSignature}
                className="text-xs font-medium text-[var(--color-gold)] hover:underline disabled:opacity-50"
                title={isAuthenticated ? "Save to your account" : "Sign up & save"}
              >
                {savingSignature ? "Saving..." : "Save"}
              </button>
            )}
            {signatureSaved && (
              <span className="text-xs text-emerald-600 flex items-center gap-1">
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                Saved
              </span>
            )}
            <span className="text-slate-300">|</span>
            <button onClick={() => setShowSigPanel(true)} className="text-xs text-slate-500 hover:text-slate-700 underline">
              Change
            </button>
          </div>
        </div>
      )}

      {/* PDF preview with placement */}
      {pages.length > 0 && !result && (
        <div>
          {pages.length > 1 && (
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="btn-secondary py-1.5 px-3 text-xs"
              >
                Prev
              </button>
              <span className="text-sm text-slate-500">
                Page {currentPage + 1} of {pages.length}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(pages.length - 1, currentPage + 1))}
                disabled={currentPage === pages.length - 1}
                className="btn-secondary py-1.5 px-3 text-xs"
              >
                Next
              </button>
            </div>
          )}

          <div
            ref={pdfContainerRef}
            className={`relative rounded-lg border border-slate-200 overflow-hidden bg-slate-100 ${
              signatureDataUrl && !showSigPanel ? "cursor-crosshair" : ""
            }`}
            onClick={handlePageClick}
          >
            <img src={pages[currentPage]} alt={`Page ${currentPage + 1}`} className="w-full select-none pointer-events-none" draggable={false} />

            {placements
              .filter((p) => p.page === currentPage)
              .map((p, idx) => {
                const globalIdx = placements.indexOf(p);
                return (
                  <div
                    key={globalIdx}
                    className={`absolute rounded border-2 transition-shadow ${
                      draggingIdx === globalIdx
                        ? "border-blue-500 shadow-lg z-20"
                        : "border-[var(--color-gold)] hover:shadow-md z-10"
                    }`}
                    style={{
                      left: `${p.x * 100}%`,
                      top: `${p.y * 100}%`,
                      width: `${p.w * 100}%`,
                      height: `${p.h * 100}%`,
                      cursor: draggingIdx === globalIdx ? "grabbing" : "grab",
                    }}
                    onMouseDown={(e) => handleDragStart(e, globalIdx)}
                  >
                    <img
                      src={p.displayUrl}
                      alt="Placed signature"
                      className="w-full h-full object-contain pointer-events-none select-none"
                      draggable={false}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removePlacement(globalIdx);
                      }}
                      className="absolute -top-2.5 -right-2.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs hover:bg-red-600 shadow-sm"
                    >
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {placements.length > 0 && !result && (
        <p className="text-sm text-slate-500">
          {placements.length} signature{placements.length !== 1 ? "s" : ""} placed
          <span className="text-slate-400 ml-1">— drag to reposition</span>
        </p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {!result && (
        <div className="flex gap-3">
          <button onClick={reset} className="btn-secondary flex-1 py-3">
            Start over
          </button>
          <button onClick={submit} disabled={processing || placements.length === 0} className="btn-primary flex-1 py-3">
            Download signed PDF
          </button>
        </div>
      )}
    </div>
  );
}
